import React, { Children, isValidElement, ReactNode } from 'react'
import { MoreVertical } from 'lucide-react'

import { cn } from '@/lib/utils.ts'
import { useIsMobile } from '@/hooks/use-mobile'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

interface PageTitleProps {
  className?: string
  children: ReactNode
}

export const PageTitle: React.FC<PageTitleProps> = ({
  className,
  children,
}) => (
  <h1 className={cn('mb-4 text-xl font-bold tracking-tight', className)}>
    {children}
  </h1>
)

interface PageTitleActionsProps {
  children: ReactNode
}

export const PageTitleActions: React.FC<PageTitleActionsProps> = ({
  children,
}) => {
  const isMobile = useIsMobile()
  const actions = Children.toArray(children).filter(Boolean)

  if (isMobile && actions.length > 1) {
    // Render a row: title left, dropdown trigger right
    return (
      <div className="flex shrink-0 grow-0 flex-row items-center justify-end">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="bg-background hover:bg-accent focus:ring-accent inline-flex items-center justify-center rounded-md border p-2 text-sm font-medium shadow-sm focus:outline-none focus:ring-2"
              aria-label="Azioni"
            >
              <MoreVertical className="size-5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {actions.map((action, idx) => {
              if (isValidElement(action)) {
                // Try to extract icon and label from button children
                const btnChildren = Children.toArray(action.props.children)
                const icon = btnChildren.find(
                  (child) =>
                    isValidElement(child) &&
                    child.type &&
                    typeof child.type !== 'string'
                )
                const label = btnChildren.find(
                  (child) =>
                    typeof child === 'string' ||
                    (isValidElement(child) && typeof child.type === 'string')
                )
                return (
                  <DropdownMenuItem
                    key={idx}
                    onClick={action.props.onClick}
                    className="gap-2"
                  >
                    {icon}
                    {label}
                  </DropdownMenuItem>
                )
              }
              return null
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    )
  }

  // Desktop or only one action: render inline, right-aligned
  return (
    <div className="flex min-w-0 shrink-0 grow-0 flex-row flex-wrap justify-end gap-2">
      {actions}
    </div>
  )
}
